import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteConfirmModal from "../global/DeleteConfirmModal";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SBD_SERVICES } from "../../utils/testData";
import { formatAmount } from "../../utils/utils";

const BRAND = "#194049";
const PER_PAGE = 10;

// ---------------------------------------------------------------------------
// Shared table header style
// ---------------------------------------------------------------------------
const thStyle = {
  padding: "10px 16px",
  textAlign: "left",
  fontSize: 11,
  color: "#6b7280",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  whiteSpace: "nowrap",
};

// ---------------------------------------------------------------------------
// Pagination (Prev / Next)
// ---------------------------------------------------------------------------
function Pagination({ page, totalPages, onChange }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 3,
        py: 2,
        borderTop: "1px solid #e5e7eb",
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
        Page {page + 1} of {Math.max(totalPages, 1)}
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        {["Prev", "Next"].map((label) => {
          const disabled =
            (label === "Prev" && page === 0) ||
            (label === "Next" && page >= totalPages - 1);
          return (
            <Box
              key={label}
              onClick={() =>
                !disabled &&
                onChange(label === "Prev" ? Math.max(0, page - 1) : Math.min(totalPages - 1, page + 1))
              }
              sx={{
                px: 2,
                py: 0.75,
                fontSize: 12,
                borderRadius: "6px",
                border: "1px solid #e5e7eb",
                cursor: disabled ? "default" : "pointer",
                bgcolor: "#fff",
                color: "#374151",
                "&:hover": { bgcolor: disabled ? "#fff" : "#f9fafb" },
                opacity: disabled ? 0.4 : 1,
                pointerEvents: disabled ? "none" : "auto",
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Category Modal (create + edit)
// ---------------------------------------------------------------------------
function CategoryModal({ initial, onClose, onSaved }) {
  const editing = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Category name is required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateDoc(doc(db, "service-categories", initial.id), {
          name: name.trim(),
          description: description.trim(),
        });
        toast.success("Category updated");
      } else {
        const ref = await addDoc(collection(db, "service-categories"), {
          name: name.trim(),
          description: description.trim(),
          createdAt: serverTimestamp(),
        });
        toast.success("Category created");
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Backdrop
      open
      onClick={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, alignItems: "flex-start", pt: { xs: 4, md: 8 } }}
    >
      <Paper
        onClick={(e) => e.stopPropagation()}
        elevation={4}
        sx={{
          width: { xs: "92vw", sm: 480 },
          maxHeight: "80vh",
          overflowY: "auto",
          borderRadius: "16px",
          p: 0,
          outline: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid #e5e7eb",
            position: "sticky",
            top: 0,
            bgcolor: "#fff",
            zIndex: 1,
            borderRadius: "16px 16px 0 0",
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
            {editing ? "Edit Category" : "Add Category"}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "#6b7280" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", mb: 0.75 }}>
              Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Hair Installations"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", mb: 0.75 }}>
              Description
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              placeholder="Brief description of this category"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Box>

          <Divider />

          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
            <Box
              onClick={onClose}
              sx={{
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                cursor: "pointer",
                color: "#374151",
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#f9fafb" },
              }}
            >
              Cancel
            </Box>
            <Box
              onClick={!saving ? handleSave : undefined}
              sx={{
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                borderRadius: "8px",
                cursor: saving ? "default" : "pointer",
                color: "#fff",
                bgcolor: BRAND,
                opacity: saving ? 0.7 : 1,
                "&:hover": { opacity: saving ? 0.7 : 0.9 },
              }}
            >
              {saving ? "Saving…" : "Save Category"}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Backdrop>
  );
}

// ---------------------------------------------------------------------------
// Service Modal (create + edit)
// ---------------------------------------------------------------------------
const TIME_SLOTS = ["11am - 1pm", "1pm - 3pm", "4pm - 6pm", "6pm - 8pm"];

function ServiceModal({ initial, categories, onClose, onSaved }) {
  const editing = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name || "");
  const [price, setPrice] = useState(initial?.price != null ? String(initial.price) : "");
  const [categoryId, setCategoryId] = useState(initial?.categoryId || "");
  const [timeframe, setTimeframe] = useState(
    Array.isArray(initial?.timeframe) ? initial.timeframe : []
  );
  const [saving, setSaving] = useState(false);

  const toggleSlot = (slot) =>
    setTimeframe((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
    );

  const selectedCategory = categories.find((c) => c.id === categoryId);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Service name is required");
      return;
    }
    const parsedPrice = parseFloat(price);
    if (isNaN(parsedPrice) || parsedPrice < 0) {
      toast.error("Enter a valid price");
      return;
    }
    if (!categoryId) {
      toast.error("Please select a category");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        price: parsedPrice,
        categoryId,
        category: selectedCategory?.name || "",
        timeframe,
      };
      if (editing) {
        await updateDoc(doc(db, "services", initial.id), payload);
        toast.success("Service updated");
      } else {
        await addDoc(collection(db, "services"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        toast.success("Service created");
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Backdrop
      open
      onClick={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, alignItems: "flex-start", pt: { xs: 4, md: 8 } }}
    >
      <Paper
        onClick={(e) => e.stopPropagation()}
        elevation={4}
        sx={{
          width: { xs: "92vw", sm: 480 },
          maxHeight: "80vh",
          overflowY: "auto",
          borderRadius: "16px",
          p: 0,
          outline: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid #e5e7eb",
            position: "sticky",
            top: 0,
            bgcolor: "#fff",
            zIndex: 1,
            borderRadius: "16px 16px 0 0",
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
            {editing ? "Edit Service" : "Add Service"}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "#6b7280" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", mb: 0.75 }}>
              Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Lace Frontal Install"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", mb: 0.75 }}>
              Price (GBP)
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="e.g. 110"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
              sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", mb: 0.75 }}>
              Category
            </Typography>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              sx={{
                bgcolor: "#fff",
                borderRadius: "8px",
                fontSize: 13,
                "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
              }}
              renderValue={(v) =>
                v ? (categories.find((c) => c.id === v)?.name || v) : (
                  <Typography sx={{ fontSize: 13, color: "#9ca3af" }}>Select a category</Typography>
                )
              }
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: 13 }}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", mb: 1 }}>
              Available Time Slots
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {TIME_SLOTS.map((slot) => {
                const active = timeframe.includes(slot);
                return (
                  <Box
                    key={slot}
                    onClick={() => toggleSlot(slot)}
                    sx={{
                      px: 2, py: 0.75, fontSize: 12, fontWeight: 500,
                      borderRadius: "20px", border: "1px solid",
                      borderColor: active ? "#194049" : "#e5e7eb",
                      bgcolor: active ? "#194049" : "#fff",
                      color: active ? "#fff" : "#374151",
                      cursor: "pointer", userSelect: "none",
                      transition: "all 0.15s",
                    }}
                  >
                    {slot}
                  </Box>
                );
              })}
            </Box>
            {timeframe.length === 0 && (
              <Typography sx={{ fontSize: 11, color: "#f59e0b", mt: 0.75 }}>
                Select at least one time slot
              </Typography>
            )}
          </Box>

          <Divider />

          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
            <Box
              onClick={onClose}
              sx={{
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                cursor: "pointer",
                color: "#374151",
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#f9fafb" },
              }}
            >
              Cancel
            </Box>
            <Box
              onClick={!saving ? handleSave : undefined}
              sx={{
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                borderRadius: "8px",
                cursor: saving ? "default" : "pointer",
                color: "#fff",
                bgcolor: BRAND,
                opacity: saving ? 0.7 : 1,
                "&:hover": { opacity: saving ? 0.7 : 0.9 },
              }}
            >
              {saving ? "Saving…" : "Save Service"}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Backdrop>
  );
}

// ---------------------------------------------------------------------------
// Services Tab
// ---------------------------------------------------------------------------
function ServicesTab({ services, categories, onRefresh }) {
  const [page, setPage] = useState(0);
  const [serviceModal, setServiceModal] = useState(null); // null | "create" | { id, ...doc }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const totalPages = Math.ceil(services.length / PER_PAGE);
  const paged = services.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, "services", deleteTarget.id));
      toast.success(`"${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete service");
    }
  };

  return (
    <>
      {/* Toolbar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
          {services.length} service{services.length !== 1 ? "s" : ""}
        </Typography>
        <Box
          onClick={() => setServiceModal("create")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 2,
            py: 0.875,
            fontSize: 13,
            fontWeight: 600,
            borderRadius: "8px",
            cursor: "pointer",
            color: "#fff",
            bgcolor: "#16a34a",
            "&:hover": { bgcolor: "#15803d" },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          Add Service
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {paged.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>No services yet</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
                  {["Name", "Category", "Price", "Timeframe", "", ""].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((svc) => (
                  <tr key={svc.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 500, color: "#111827" }}>
                      {svc.name}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>
                      {svc.category || "—"}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "#111827", whiteSpace: "nowrap" }}>
                      {formatAmount(svc.price)}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      {Array.isArray(svc.timeframe) && svc.timeframe.length > 0
                        ? svc.timeframe.map((t) => (
                            <Chip key={t} label={t} size="small" sx={{ fontSize: 10, height: 18, mr: 0.5, mb: 0.25, bgcolor: "#f3f4f6", color: "#374151" }} />
                          ))
                        : <span style={{ fontSize: 13, color: "#9ca3af" }}>—</span>
                      }
                    </td>
                    <td style={{ padding: "8px 8px 8px 16px", width: 40 }}>
                      <IconButton
                        size="small"
                        onClick={() => setServiceModal(svc)}
                        sx={{
                          "&:hover": { bgcolor: "#e0f2f1", color: BRAND },
                          color: "#9ca3af",
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </td>
                    <td style={{ padding: "8px 16px 8px 4px", width: 40 }}>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(svc)}
                        sx={{
                          "&:hover": { bgcolor: "#fee2e2", color: "#ef4444" },
                          color: "#9ca3af",
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}

        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </Box>

      {/* Modals */}
      {serviceModal === "create" && (
        <ServiceModal
          categories={categories}
          onClose={() => setServiceModal(null)}
          onSaved={onRefresh}
        />
      )}
      {serviceModal && serviceModal !== "create" && (
        <ServiceModal
          initial={serviceModal}
          categories={categories}
          onClose={() => setServiceModal(null)}
          onSaved={onRefresh}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          item={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Categories Tab
// ---------------------------------------------------------------------------
function CategoriesTab({ categories, onRefreshCategories, onRefreshAll }) {
  const [page, setPage] = useState(0);
  const [categoryModal, setCategoryModal] = useState(null); // null | "create" | { id, ...doc }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const totalPages = Math.ceil(categories.length / PER_PAGE);
  const paged = categories.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, "service-categories", deleteTarget.id));
      toast.success(`"${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      onRefreshAll();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete category");
    }
  };

  return (
    <>
      {/* Toolbar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
          {categories.length} categor{categories.length !== 1 ? "ies" : "y"}
        </Typography>
        <Box
          onClick={() => setCategoryModal("create")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 2,
            py: 0.875,
            fontSize: 13,
            fontWeight: 600,
            borderRadius: "8px",
            cursor: "pointer",
            color: "#fff",
            bgcolor: "#16a34a",
            "&:hover": { bgcolor: "#15803d" },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          Add Category
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {paged.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>No categories yet</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
                  {["Name", "Description", "", ""].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((cat) => (
                  <tr key={cat.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "#111827", whiteSpace: "nowrap" }}>
                      {cat.name}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#6b7280", maxWidth: 360 }}>
                      {cat.description || "—"}
                    </td>
                    <td style={{ padding: "8px 8px 8px 16px", width: 40 }}>
                      <IconButton
                        size="small"
                        onClick={() => setCategoryModal(cat)}
                        sx={{
                          "&:hover": { bgcolor: "#e0f2f1", color: BRAND },
                          color: "#9ca3af",
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </td>
                    <td style={{ padding: "8px 16px 8px 4px", width: 40 }}>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(cat)}
                        sx={{
                          "&:hover": { bgcolor: "#fee2e2", color: "#ef4444" },
                          color: "#9ca3af",
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}

        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </Box>

      {/* Modals */}
      {categoryModal === "create" && (
        <CategoryModal
          onClose={() => setCategoryModal(null)}
          onSaved={onRefreshAll}
        />
      )}
      {categoryModal && categoryModal !== "create" && (
        <CategoryModal
          initial={categoryModal}
          onClose={() => setCategoryModal(null)}
          onSaved={onRefreshAll}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          item={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function AdminServicesManager() {
  const [activeTab, setActiveTab] = useState(0); // 0 = Services, 1 = Categories
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchCategories = async () => {
    const snap = await getDocs(
      query(collection(db, "service-categories"), orderBy("createdAt", "asc"))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  const fetchServices = async () => {
    const snap = await getDocs(
      query(collection(db, "services"), orderBy("createdAt", "asc"))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  const loadAll = async () => {
    try {
      const [cats, svcs] = await Promise.all([fetchCategories(), fetchServices()]);
      setCategories(cats);
      setServices(svcs);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      for (const group of SBD_SERVICES) {
        const catRef = await addDoc(collection(db, "service-categories"), {
          name: group.category,
          description: group.description || "",
          createdAt: serverTimestamp(),
        });
        for (const svc of group.services) {
          await addDoc(collection(db, "services"), {
            name: svc.name,
            price: svc.price,
            categoryId: catRef.id,
            category: group.category,
            timeframe: [...TIME_SLOTS],
            createdAt: serverTimestamp(),
          });
        }
      }
      toast.success("Default services seeded successfully");
      await loadAll();
    } catch (err) {
      console.error(err);
      toast.error("Seeding failed");
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: BRAND }} />
      </Box>
    );
  }

  const tabs = ["Services", "Categories"];

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Pill Tab Switcher */}
      <Box
        sx={{
          display: "inline-flex",
          bgcolor: "#f3f4f6",
          borderRadius: "10px",
          p: 0.5,
          mb: 3,
          gap: 0.5,
        }}
      >
        {tabs.map((label, idx) => (
          <Box
            key={label}
            onClick={() => setActiveTab(idx)}
            sx={{
              px: 3,
              py: 0.875,
              fontSize: 13,
              fontWeight: 600,
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.15s",
              color: activeTab === idx ? "#fff" : "#6b7280",
              bgcolor: activeTab === idx ? BRAND : "transparent",
              "&:hover": activeTab !== idx ? { bgcolor: "#e5e7eb" } : {},
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      {/* Seed button (only when services list is empty) */}
      {services.length === 0 && (
        <Box sx={{ mb: 2 }}>
          <Box
            onClick={!seeding ? handleSeed : undefined}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 2,
              py: 0.75,
              fontSize: 12,
              fontWeight: 600,
              borderRadius: "8px",
              border: `1px dashed ${BRAND}`,
              cursor: seeding ? "default" : "pointer",
              color: BRAND,
              bgcolor: "#f0f9fa",
              opacity: seeding ? 0.6 : 1,
              "&:hover": { bgcolor: "#e0f2f1" },
            }}
          >
            {seeding ? "Seeding…" : "Seed defaults"}
          </Box>
          <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 0.5 }}>
            Bulk-insert the default SBD service categories and services. Disappears once seeded.
          </Typography>
        </Box>
      )}

      {/* Tab content */}
      {activeTab === 0 && (
        <ServicesTab
          services={services}
          categories={categories}
          onRefresh={loadAll}
        />
      )}
      {activeTab === 1 && (
        <CategoriesTab
          categories={categories}
          onRefreshCategories={fetchCategories}
          onRefreshAll={loadAll}
        />
      )}
    </>
  );
}
